/**
 * Takes the credentials of a concierge link out of the address bar before
 * anything else in the page can read them (KAN-262).
 *
 * A concierge link is `/enter?token=...` for a salon and
 * `/enter?token=...&cap=...` for an operator. Both are credentials, and posthog
 * reads `location.href` itself - for the initial person properties it stores and
 * posts to `/flags/`, and for its session-entry properties - along a path that
 * `before_send` never sees. posthog masks a fixed list of parameter names there
 * and `cap` was never on it; extending the list would only protect the names
 * somebody remembered. The only airtight answer is for the URL it reads to carry
 * nothing: this runs before the router exists, keeps the values for `EnterPage`,
 * and rewrites the URL to the bare path. The same rewrite keeps the credentials
 * out of the browser history and out of any `Referer`.
 *
 * The values are kept in memory and mirrored to `sessionStorage` (tab-scoped,
 * the same place the capability token ends up anyway), so a reload of `/enter`
 * after a transient redemption failure can still retry instead of dead-ending on
 * "missing its code". `EnterPage` clears them once the link is redeemed.
 *
 * Deliberately limited to `/enter`, which is only ever reached by loading the
 * link (nothing in the app navigates there with a token). Other routes that
 * carry a credential (`/reset-password`, `/supplier-match/confirm`) rely on
 * posthog's name masking, which lists `token` and `email`; the e2e suite proves
 * neither reaches the wire.
 */

import { ENTER_PATH } from "../navigation/routes";

const STORAGE_KEY = "ss_link_credentials";

export interface LinkCredentials {
  token?: string;
  cap?: string;
  /**
   * Where the link wants the salon to land after it is redeemed. Not a
   * credential, but it travels with them: it rides the same URL, and the same
   * rewrite that hides `token` would otherwise throw it away. The approval
   * email's activation link carries `next=activate` (KAN-291).
   */
  next?: string;
}

let credentials: LinkCredentials = {};

function readStored(): LinkCredentials {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LinkCredentials) : {};
  } catch {
    return {};
  }
}

/** The credentials taken from this tab's concierge link, if there are any. */
export function readLinkCredentials(): LinkCredentials {
  return credentials.token ? credentials : readStored();
}

/** Forget the credentials, once the link has been redeemed. */
export function clearLinkCredentials(): void {
  credentials = {};
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: there was nothing persisted to remove.
  }
}

/**
 * If the current URL is a concierge link, move its `token`, `cap` and `next` out of it
 * and replace the URL with the bare path. Anything else is left alone.
 *
 * Must run before the router is created (it reads the location at creation) and
 * before analytics initialises. The history entry's own state is preserved.
 */
export function stripLinkCredentials(
  location: Pick<Location, "pathname" | "search" | "hash"> = window.location,
  history: Pick<History, "state" | "replaceState"> = window.history
): void {
  if (location.pathname.replace(/\/+$/, "") !== ENTER_PATH) return;
  if (!location.search && !location.hash) return;

  const params = new URLSearchParams(location.search);
  const taken = {
    token: params.get("token") ?? undefined,
    cap: params.get("cap") ?? undefined,
    next: params.get("next") ?? undefined
  };
  // A visit with no token must not wipe the credentials an earlier one left
  // for a retry.
  if (taken.token) {
    credentials = taken;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(taken));
    } catch {
      // Memory alone still serves this page load; only a reload loses it.
    }
  }

  // The whole query and the fragment go, not just the two known names: nothing
  // on this route reads any other, and a second credential parameter added to
  // the link one day must not start leaking because nobody updated a list.
  history.replaceState(history.state, "", location.pathname);
}
